<template>
  <!-- Categories compared by size, as horizontal bars: one series, so one
       colour, the value at each bar's tip, the longest bar filling the track.
       Each row is its own hover target, and the table view lists the same
       numbers with their share of the total. -->
  <div class="bc" :class="{ 'is-loading': loading }" :aria-busy="loading ? 'true' : 'false'">
    <p v-if="!items.length" class="bc-empty">{{ emptyText }}</p>

    <div v-else-if="view === 'table'" class="chart-table-wrap">
      <table class="chart-table" :aria-label="label">
        <thead>
          <tr>
            <th scope="col">{{ categoryLabel }}</th>
            <th scope="col" class="is-num">{{ valueHeading }}</th>
            <th scope="col" class="is-num">Share</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.key">
            <th scope="row">
              {{ item.label }}
              <span v-if="item.sublabel" class="chart-table__sub">{{ item.sublabel }}</span>
            </th>
            <td class="is-num">{{ format(item.value) }}</td>
            <td class="is-num">{{ shareOf(item) }}%</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td class="is-num">{{ format(total) }}</td>
            <td class="is-num">{{ total ? 100 : 0 }}%</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <ul v-else class="bc-list" :aria-label="label">
      <li v-for="item in items" :key="item.key" class="bc-item">
        <component
          :is="selectable ? 'button' : 'div'"
          class="bc-row"
          :class="{ 'is-selectable': selectable }"
          v-bind="selectable ? { type: 'button' } : {}"
          :title="`${item.label}: ${format(item.value)} ${valueLabel}`"
          @click="onSelect(item)"
        >
          <span class="bc-row__label">
            <span class="bc-row__name">{{ item.label }}</span>
            <span v-if="item.sublabel" class="bc-row__sub">{{ item.sublabel }}</span>
          </span>
          <span class="bc-row__track" aria-hidden="true">
            <span
              class="bc-row__bar"
              :class="{ 'is-zero': !item.value }"
              :style="{ width: `${widthOf(item)}%`, background: item.color || color }"
            ></span>
          </span>
          <span class="bc-row__value">{{ format(item.value) }}</span>
        </component>
      </li>
    </ul>
  </div>
</template>
<script>
import { CHART_COLORS, percentOf } from '@/utils/dashboard';

export default {
  name: 'BarChart',
  props: {
    // [{ key, label, value, sublabel?, color? }], in the order to show.
    items: { type: Array, default: () => [] },
    color: { type: String, default: CHART_COLORS.accent },
    view: { type: String, default: 'chart' },
    label: { type: String, default: 'Chart' },
    categoryLabel: { type: String, default: 'Category' },
    // What the numbers count: "events", "users".
    valueLabel: { type: String, default: 'events' },
    // Rows can be chosen (they are buttons, and `select` says which).
    selectable: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'Nothing in this period' },
  },
  emits: ['select'],
  computed: {
    max() {
      return this.items.reduce((max, item) => Math.max(max, this.valueOf(item)), 0);
    },
    total() {
      return this.items.reduce((sum, item) => sum + this.valueOf(item), 0);
    },
    valueHeading() {
      const text = String(this.valueLabel || '');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
  },
  methods: {
    valueOf(item) {
      return Math.max(0, Number(item.value) || 0);
    },
    format(value) {
      return (Number(value) || 0).toLocaleString('en-US');
    },
    widthOf(item) {
      return this.max ? (this.valueOf(item) / this.max) * 100 : 0;
    },
    shareOf(item) {
      return percentOf(this.valueOf(item), this.total);
    },
    onSelect(item) {
      if (this.selectable) this.$emit('select', item);
    },
  },
};
</script>
<style scoped>
.bc {
  min-width: 0;
  transition: opacity .15s ease;
}

.bc.is-loading {
  opacity: 0.6;
}

.bc-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.bc-item {
  list-style: none;
}

.bc-row {
  display: grid;
  grid-template-columns: minmax(96px, 36%) minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 34px;
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
}

.bc-row.is-selectable {
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.bc-row.is-selectable:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border) !important;
}

.bc-row.is-selectable:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus) !important;
}

/* The hovered mark lifts. */
.bc-row:hover .bc-row__bar {
  filter: brightness(1.12);
}

.bc-row__label {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bc-row__name {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--fs-sm);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bc-row__sub {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bc-row__track {
  display: block;
  height: 10px;
  min-width: 0;
}

/* Square at the baseline, rounded at the data end. */
.bc-row__bar {
  display: block;
  height: 100%;
  min-width: 3px;
  border-radius: 0 4px 4px 0;
  transition: width .25s ease, filter .15s ease;
}

.bc-row__bar.is-zero {
  min-width: 0;
}

.bc-row__value {
  min-width: 36px;
  color: var(--color-text-secondary);
  font-size: var(--fs-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.bc-empty {
  margin: 0;
  padding: 28px 12px;
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
  text-align: center;
}

/* ------------------------------ table view ------------------------------ */
.chart-table-wrap {
  overflow: auto;
  max-height: 360px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.chart-table {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
  font-size: var(--fs-sm);
}

.chart-table th,
.chart-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-divider);
  color: var(--color-text-primary);
  text-align: left;
  vertical-align: top;
}

.chart-table thead th {
  position: sticky;
  top: 0;
  background: var(--color-bg-soft);
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.chart-table tbody th {
  font-weight: 500;
}

.chart-table tfoot th,
.chart-table tfoot td {
  border-bottom: 0;
  background: var(--color-bg-soft);
  font-weight: 700;
}

.chart-table .is-num {
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.chart-table__sub {
  display: block;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
}

@media (max-width: 575.98px) {
  .bc-row {
    grid-template-columns: minmax(80px, 40%) minmax(0, 1fr) auto;
    gap: 8px;
  }
}
</style>
